import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataLabel, fmtDate } from "@/lib/utils-ui";
import { toast } from "sonner";
import { Plus, Upload, FileText, Pencil, Trash2 } from "lucide-react";
import ExportMenu from "@/components/ExportMenu";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function AuditsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ title: "", audit_type: "Internal IT", audit_date: "", tags: "", notes: "", company_id: "" });
  const fileInputs = useRef({});

  const load = async () => { const { data } = await api.get("/audits"); setItems(data); };
  useEffect(() => { load(); api.get("/companies").then(r => setCompanies(r.data)); }, []);

  const create = async () => {
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map(s => s.trim()).filter(Boolean) : form.tags };
      if (user.role !== "super_admin") delete payload.company_id;
      if (editing) {
        await api.put(`/audits/${editing.id}`, payload);
        toast.success("Audit updated");
      } else {
        await api.post("/audits", payload);
        toast.success("Audit created");
      }
      setOpen(false); setEditing(null); load();
      setForm({ title: "", audit_type: "Internal IT", audit_date: "", tags: "", notes: "", company_id: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title, audit_type: a.audit_type, audit_date: a.audit_date || "",
      tags: (a.tags || []).join(", "), notes: a.notes || "", company_id: a.company_id,
    });
    setOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/audits/${deleting.id}`);
      toast.success("Audit deleted");
      setDeleting(null); load();
    } catch (e) { toast.error("Failed"); }
  };

  const upload = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    await api.post(`/audits/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }});
    toast.success("File uploaded"); load();
  };

  const canEdit = ["super_admin","company_admin","it_admin","auditor"].includes(user?.role);

  return (
    <div className="p-8 space-y-6" data-testid="audits-page">
      <div className="flex items-start justify-between">
        <div>
          <DataLabel>Module 03</DataLabel>
          <h1 className="font-head font-black uppercase text-4xl tracking-tighter mt-1">Audits</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload, version and schedule IT audit reports.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <ExportMenu module="audits" />
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-audit-btn" className="bg-black text-white hover:bg-[#FF5000] h-10 uppercase tracking-wider text-xs font-head"><Plus size={14} className="mr-1"/>New Audit</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-head uppercase tracking-tight">New Audit Entry</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {user.role === "super_admin" && (
                  <div><Label className="data-label">Company</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({...form, company_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                      <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label className="data-label">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})}/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="data-label">Audit Type</Label><Input value={form.audit_type} onChange={e => setForm({...form, audit_type: e.target.value})}/></div>
                  <div><Label className="data-label">Date</Label><Input type="date" value={form.audit_date} onChange={e => setForm({...form, audit_date: e.target.value})}/></div>
                </div>
                <div><Label className="data-label">Tags (comma sep)</Label><Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})}/></div>
                <div><Label className="data-label">Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}/></div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-black text-white hover:bg-[#FF5000]">{editing ? "Save" : "Create"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <ConfirmDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete audit?" description={deleting?.title} confirmLabel="Delete" variant="destructive"
        onConfirm={confirmDelete} testid="delete-audit" />

      <div className="border border-border bg-white overflow-x-auto" data-testid="audit-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-muted">
            {["TITLE","TYPE","DATE","VERSION","TAGS","FILE","ACTIONS"].map(h => <th key={h} className="text-left p-3 data-label">{h}</th>)}
          </tr></thead>
          <tbody>
            {items.map(a => (
              <tr key={a.id} className="border-b border-border hover-row" data-testid={`audit-row-${a.id}`}>
                <td className="p-3 font-semibold">{a.title}</td>
                <td className="p-3 text-xs uppercase">{a.audit_type}</td>
                <td className="p-3 font-mono text-xs">{fmtDate(a.audit_date)}</td>
                <td className="p-3 font-mono text-xs">v{a.version}</td>
                <td className="p-3 text-xs">{(a.tags || []).join(", ") || "—"}</td>
                <td className="p-3 text-xs">
                  {a.file_url ? (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${a.file_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-[#FF5000]"><FileText size={12}/>{a.file_name}</a>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <input ref={el => fileInputs.current[a.id] = el} type="file" className="hidden"
                           data-testid={`audit-file-input-${a.id}`}
                           onChange={(e) => e.target.files[0] && upload(a.id, e.target.files[0])}/>
                    <button onClick={() => fileInputs.current[a.id]?.click()} data-testid={`audit-upload-btn-${a.id}`} className="hover:text-[#FF5000]" title="Upload file"><Upload size={14}/></button>
                    {canEdit && <button data-testid={`edit-audit-${a.id}`} onClick={() => startEdit(a)} title="Edit" className="hover:text-[#FF5000]"><Pencil size={14}/></button>}
                    {canEdit && <button data-testid={`delete-audit-${a.id}`} onClick={() => setDeleting(a)} title="Delete" className="hover:text-[#FF3B30]"><Trash2 size={14}/></button>}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No audits.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

